import { useEffect } from "react";

import "./PrivacyPolicy.css";

const TOC = [
  "What Information Do We Collect?",
  "How Do We Process Your Information?",
  "Legal Bases for Processing",
  "Sharing Personal Information",
  "Data Retention",
  "Data Security",
  "Your Privacy Rights",
  "Track Features",
  "Policy Updates",
  "Contact Information",
];

const StackClaskPrivacyPolicy = () => {
  useEffect(() => {
    const smoothScroll = (e: any) => {
      e.preventDefault();
      const targetId = e.target.getAttribute("href").slice(1);
      const targetElement = document.getElementById(targetId);
      targetElement?.scrollIntoView({ behavior: "smooth" });
    };

    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener("click", smoothScroll);
    });

    return () => {
      document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        anchor.removeEventListener("click", smoothScroll);
      });
    };
  }, []);

  return (
    <div className="privacy-policy privacy-policy--stackclash">
      <header className="pp-header">
        <h1>Privacy Policy</h1>
        <p className="pp-updated">Last updated Jul 7, 2026</p>
      </header>

      <section className="pp-summary pp-card">
        <h2>Summary of Key Points</h2>
        <ul>
          <li>
            We process personal information based on your interactions and legal
            requirements
          </li>
          <li>No sensitive personal information is processed</li>
          <li>Data security measures in place including encryption</li>
        </ul>
      </section>

      <nav className="pp-toc" aria-label="Table of contents">
        <h2>Table of Contents</h2>
        <ol>
          {TOC.map((title, i) => (
            <li key={title}>
              <a href={`#section-${i + 1}`}>{title}</a>
            </li>
          ))}
        </ol>
      </nav>

      <section id="section-1" className="pp-section">
        <h3>1. What Information Do We Collect?</h3>
        <div className="pp-card">
          <p>We collect personal information you voluntarily provide:</p>
          <ul>
            <li>Device Id</li>
            <li>Best Score</li>
            <li>Music / Vibration Preferences</li>
          </ul>
        </div>
      </section>

      <section id="section-2" className="pp-section">
        <h3>2. How Do We Process Your Information?</h3>
        <div className="pp-card">
          <p>We process information to:</p>
          <ul>
            <li>Visualize & Analyze game's data</li>
            <li>Provide better experience to users</li>
            <li>Improve game's quality</li>
          </ul>
        </div>
      </section>

      <section id="section-3" className="pp-section">
        <h3>3. Legal Bases for Processing</h3>
        <div className="pp-card">
          <p>We process information based on these legal grounds:</p>
          <ul>
            <li>
              <strong>Consent:</strong> Clear permission for specific purposes
            </li>
            <li>
              <strong>Contractual Needs:</strong> To fulfill our service
              obligations
            </li>
            <li>
              <strong>Legal Compliance:</strong> When required by law
            </li>
            <li>
              <strong>Vital Interests:</strong> To protect someone's safety
            </li>
          </ul>
        </div>
      </section>

      <section id="section-4" className="pp-section">
        <h3>4. Sharing Personal Information</h3>
        <div className="pp-card">
          <p>We do not share any personal information</p>
        </div>
      </section>

      <section id="section-5" className="pp-section">
        <h3>5. Data Retention</h3>
        <div className="pp-card">
          <p>Data are retained for only 90 days by our third party service.</p>
        </div>
      </section>

      <section id="section-6" className="pp-section">
        <h3>6. Data Security</h3>
        <div className="pp-card">
          <p>
            Database services are managed by trusted third-party providers with
            strong security practices
          </p>
          <p>
            <strong>Important:</strong> No system is 100% secure - we recommend
            using strong passwords
          </p>
        </div>
      </section>

      <section id="section-7" className="pp-section">
        <h3>7. Your Privacy Rights</h3>
        <div className="pp-card">
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Request access to your data</li>
            <li>Correct inaccurate information</li>
            <li>Delete your personal data</li>
          </ul>
          <p>
            Email us at konstantinos.efkarpidis@outlook.com or use in-app
            deletion feature
          </p>
        </div>
      </section>

      <section id="section-8" className="pp-section">
        <h3>8. Track Features</h3>
        <div className="pp-card">
          <p>
            We currently use a third party service to track user's behavior. You
            data are safe and never shared with anyone else.
          </p>
        </div>
      </section>

      <section id="section-9" className="pp-section">
        <h3>9. Policy Updates</h3>
        <div className="pp-card">
          <p>
            We update this policy annually or when required by law. Material
            changes will be notified via:
          </p>
          <ul>
            <li>In-app banners</li>
            <li>Updated revision date</li>
          </ul>
        </div>
      </section>

      <section id="section-10" className="pp-section">
        <h3>10. Contact Information</h3>
        <div className="pp-card">
          <p>
            Konstantinos Efkarpidis
            <br />
            <a href="mailto:konstantinos.efkarpidis@outlook.com">
              konstantinos.efkarpidis@outlook.com
            </a>
          </p>
          <div className="pp-social">
            <a
              href="https://github.com/kostas64"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub profile (opens in a new tab)"
            >
              <img src={require("../../assets/github.png")} alt="Github" />
            </a>
            <a
              href="https://www.linkedin.com/in/konstantinos-efkarpidis/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn profile (opens in a new tab)"
            >
              <img src={require("../../assets/linkedin.png")} alt="Linkedin" />
            </a>
          </div>
        </div>
      </section>

      <footer className="pp-footer" />

      <button
        type="button"
        className="privacy-back-top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        ↑ Back to Top
      </button>
    </div>
  );
};

export default StackClaskPrivacyPolicy;
